import React from "react";
import { useCookies } from "react-cookie";

import { useDialog } from "../../components/ui/use-dialog";

import { TrialDialog } from "../../pages/auth/_dialog/trial";
import { LogoDialog } from "../../pages/auth/_dialog/logo";

import { LoginUserProto } from "../../core/auth/entity/login-user";
import { LoginUser } from "../../core/auth/type/login-user";
import { useAuth } from "../../hooks/use-auth";
import { useUsecases } from "../../store";

interface AuthContextType {
  token: string | null;
  loginUser: LoginUser;
  logOut: () => void;
  isLogin: boolean;
  isLoading: boolean;
  fetchClient: () => void;
}

export const initialState: LoginUser = {
  id: null,
  name: null,
  role: 0,
  email: null,
  client: {
    id: null,
    name: null,
    logo: null,
    used_size: null,
    used_code: null,
    role_requests: [],
    is_waiting_review: false,
    is_approved_review: false,
    is_use_assistant: false,
    is_new_db: false,
    platform_url: null,
  },
  plan: {
    plan_id: null,
    plan_type: null,
    is_plan_active: false,
    is_mobile_link: false,
    is_multi_project: false,
    fee_type: "capacity",
    pay_type: "card",
    limit_size: null,
    node_size: null,
  },
  projects: [],
  trial: { status: null, expire: null, unix: null },
};

const AuthContext = React.createContext<AuthContextType>({
  token: null,
  loginUser: initialState,
  logOut: () => {},
  isLogin: false,
  isLoading: true,
  fetchClient: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [cookies] = useCookies(["trial", "logo"]);
  const auth = useAuth();
  const { dataManagerUsecase } = useUsecases();

  const trialDialog = useDialog();
  const logoDialog = useDialog();

  const { isLogin, isLoading, loginUser } = auth;
  const proto = new LoginUserProto(loginUser);
  const clientLogo = proto.clientLogo;
  const clientId = proto.clientId;
  const trial = proto.trial;
  const isNewDb = proto.isNewDb;
  // gateway 層が読む ingest mode は use-auth の onSuccess/logOut で platform_url と
  // 同期セットしている (同じ loginUser 応答由来なのでズレない)。ここでは設定しない。

  const isOpenTrial = React.useCallback(() => {
    const now = Date.now();
    const expireIn7Days =
      typeof trial.unix === "number" &&
      trial.unix * 1000 > now &&
      trial.unix * 1000 - 3 * 24 * 60 * 60 * 1000 <= now;
    const notShownYet = now > (cookies.trial || 0);
    return trial.status === 0 && expireIn7Days && notShownYet;
  }, [trial, cookies.trial]);

  const isOpenLogo = React.useCallback(() => {
    return !clientLogo && Date.now() > (cookies.logo || 0);
  }, [clientLogo, cookies.logo]);

  /**
   * ベンダーの DWH スキーマを用意する (冪等)。
   *
   * ログイン直後とプロジェクト切替直後に 1 回ずつ呼ぶ。切替は
   * ``window.location.reload()`` を伴うので、client_id をキーにした 1 回きりのガードで
   * 両方を賄える。失敗しても画面は使えるべきなので握りつぶす (usecase 側で吸収)。
   */
  const provisionedFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    // provision は 2 フェーズ取込 (DWH スキーマ前提) のときだけ。legacy では不要。
    if (!isNewDb) return;
    if (isLoading || !isLogin || !clientId) return;
    if (provisionedFor.current === clientId) return;
    provisionedFor.current = clientId;
    void dataManagerUsecase.provisionVendor();
  }, [isNewDb, isLoading, isLogin, clientId, dataManagerUsecase]);

  React.useEffect(() => {
    if (!isLoading && isLogin && clientId) {
      if (isOpenTrial()) {
        trialDialog.open();
      } else if (isOpenLogo()) {
        logoDialog.open();
      }
    }
  }, [
    isOpenTrial,
    isOpenLogo,
    trialDialog,
    logoDialog,
    isLoading,
    isLogin,
    clientId,
  ]);

  return (
    <AuthContext.Provider value={auth}>
      {children}
      {trialDialog.isOpen && (
        <TrialDialog
          onOpenChange={trialDialog.open}
          open={trialDialog.isOpen}
        />
      )}
      {logoDialog.isOpen && (
        <LogoDialog onOpenChange={logoDialog.open} open={logoDialog.isOpen} />
      )}
    </AuthContext.Provider>
  );
};

export function useLoginUser() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useLoginUser must be used within an AuthProvider");
  }
  return context;
}
